import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { AnnotationData, RaceChartData } from "@shared/types";
import { useAuth, useOptionalAuth } from "../auth/AuthContext";
import { rememberPostAuthRedirect } from "../../lib/postAuthRedirect";
import { track } from "../../lib/analytics";
import { splitTeam } from "../chart/panel-ui";
import { formatDuration } from "../../lib/prose";
import {
  displayName,
  followDriver,
  followedAs,
  rememberPendingDriver,
  sameDriver,
  takePendingDriver,
  unfollowDriver,
  useMyDrivers,
} from "./api";
import { DRIVE_FROM_LAPS, driveOf, driveText, stintsOf } from "./stints";
import { ShareRaceDriverCard } from "./driverCard";
import { useOpenRaceCards } from "../share/raceCardsContext";

const T = { line: "#1e1e38", text: "#e0e0e8", soft: "#cbd5e1", muted: "#9a9ab4", accent: "#f48c34", control: "#2c2c50" };

/** The car's drivers as the import found them: the driver list, else "Team (A / B)". */
export function carDriverNames(car: { team?: string; drivers?: string[] } | undefined): string[] {
  const listed = car?.drivers?.filter((d) => d.trim()) ?? [];
  const names = listed.length ? listed : (splitTeam(car?.team).drivers ?? "").split(/\s*[/,]\s*/).filter(Boolean);
  const out: string[] = [];
  for (const n of names) if (!out.some((o) => sameDriver(o, n))) out.push(n);
  return out;
}

/**
 * "Drivers" under the focus car on the race page: each driver's share of the race when the timing
 * says who drove when, a Follow button and (on a stored race, not RaceTrace Live) the driver's
 * share card for this race. Signed out, Follow asks for an account first and the
 * follow is done on the way back.
 */
export function CarDrivers(props: { data: RaceChartData; focusNum: number; annotations: AnnotationData | null; heading?: string }) {
  // Only inside the app (auth and router): the chart panels also render on their own.
  return useOptionalAuth() ? <CarDriversInApp {...props} /> : null;
}

function CarDriversInApp({
  data,
  focusNum,
  annotations,
  heading,
}: {
  data: RaceChartData;
  focusNum: number;
  annotations: AnnotationData | null;
  /** A label over the list, for where the car's name is not right above it. */
  heading?: string;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  // The share card needs the stored race: the race page (/chart?race=), not RaceTrace Live.
  const raceId = pathname === "/chart" ? params.get("race") : null;
  const mine = useMyDrivers(user?.id);
  // Phones: a driver's Share opens the Race cards view on their card.
  const openCards = useOpenRaceCards();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [justFollowed, setJustFollowed] = useState<string | null>(null);
  const car = data.cars[String(focusNum)];
  const names = useMemo(() => carDriverNames(car), [car]);
  const stints = useMemo(
    () => (car ? stintsOf(car.laps, annotations?.[String(focusNum)]?.pits, names.length === 1 ? names[0] : null, names, annotations?.[String(focusNum)]?.current ?? null) : []),
    [car, annotations, focusNum, names]
  );

  // A follow asked for before signing up is done now.
  useEffect(() => {
    if (!user) return;
    const pending = takePendingDriver();
    if (!pending) return;
    followDriver(pending)
      .then((r) => {
        if (r.created) {
          track("follow_driver", { source: "race_page" });
          setJustFollowed(r.driver.name);
        }
      })
      .catch(() => undefined);
  }, [user]);

  if (!car || names.length === 0) return null;

  const toggle = async (name: string) => {
    setError(null);
    if (!user) {
      rememberPendingDriver(name);
      rememberPostAuthRedirect(`${window.location.pathname}${window.location.search}`);
      navigate("/signup");
      return;
    }
    setBusy(name);
    try {
      const f = followedAs(mine, name);
      if (f) {
        await unfollowDriver(f.id);
        if (justFollowed && sameDriver(justFollowed, name)) setJustFollowed(null);
      } else {
        const r = await followDriver(name);
        if (r.created) {
          track("follow_driver", { source: "race_page" });
          setJustFollowed(r.driver.name);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "That did not work. Try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section aria-label="Drivers" className="mt-1 flex flex-col">
      {(heading || openCards) && (
        <div className="pb-1 text-[12px] font-semibold uppercase tracking-[0.06em] sm:text-[11px] sm:font-normal" style={{ color: T.muted }}>
          {heading ?? "Drivers"}
        </div>
      )}
      {names.map((n) => {
        const drive = driveOf(car.laps, stints, n, car.driveTimes);
        const f = followedAs(mine, n);
        return (
          <div key={n} className="flex min-h-[56px] items-center gap-2.5 border-t py-1.5 sm:min-h-[44px] sm:py-1" style={{ borderColor: T.line }}>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-semibold sm:text-[13px]" style={{ color: T.text }}>
                {displayName(n)}
              </div>
              {drive && drive.stints > 0 && (
                <div className="truncate text-[13px] tabular-nums sm:text-[12px]" style={{ color: T.muted }} title={drive.driveSource === "laps" ? DRIVE_FROM_LAPS : "Time at the wheel, on track (out of the pit lane)"}>
                  {drive.stints} stint{drive.stints === 1 ? "" : "s"} · {driveText(drive.driveTimeS, drive.driveSource)}
                  {drive.best ? ` · best ${formatDuration(drive.best.sec)}` : ""}
                </div>
              )}
              {raceId && !openCards && <ShareRaceDriverCard raceId={raceId} carNumber={car.label ?? String(focusNum)} driver={n} chart={data} annotations={annotations} />}
            </div>
            {raceId && openCards && (
              <button
                type="button"
                onClick={() => openCards(n)}
                aria-label={`Share ${displayName(n)}'s card`}
                data-share-open="driver"
                title={`Open ${displayName(n)}'s race card, ready to share`}
                className="flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-bold"
                style={{ background: T.accent, color: "#0b0b18" }}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12M7.5 7.5 12 3l4.5 4.5M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
                </svg>
                Share
              </button>
            )}
            <button
              type="button"
              onClick={() => toggle(n)}
              disabled={busy === n}
              aria-pressed={!!f}
              aria-label={f ? `Stop following ${displayName(n)}` : `Follow ${displayName(n)}`}
              title={f ? "Stop following (Settings › My drivers lists them all)" : "Follow this driver in every race, whatever the car"}
              className="min-h-[44px] shrink-0 rounded-full sm:min-h-[32px] border px-2.5 text-[12px] font-semibold disabled:opacity-60"
              style={
                f
                  ? { borderColor: "rgba(244,140,52,0.6)", background: "rgba(244,140,52,0.12)", color: T.accent }
                  : { borderColor: T.control, color: T.text }
              }
            >
              {f ? "✓ Following" : "+ Follow"}
            </button>
          </div>
        );
      })}
      {justFollowed && followedAs(mine, justFollowed) && (
        <p className="m-0 mt-1 text-[12px]" style={{ color: T.soft }}>
          {justFollowed}&apos;s latest race now shows on your{" "}
          <Link to="/dashboard" style={{ color: T.accent }}>
            Dashboard
          </Link>
          , whatever car they race.
        </p>
      )}
      {error && (
        <p role="alert" className="m-0 mt-1 text-[12px]" style={{ color: "#e66767" }}>
          {error}
        </p>
      )}
    </section>
  );
}
